import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { getMalawiGrade } from "../../../utils/gradingUtils";
import { ResponsiveTableWrap } from "./resultsUi";
import { reportPeriodDisplayLabel } from "../../../utils/reportPeriodUtils";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery } from "../../../utils/termRecordsScope";

export default function ClassResultsReview() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const homeroomTerm = useHomeroomTeacherTerm();
    const adminId = currentUser?._id || currentUser?.id;
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectId, setRejectId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [loadError, setLoadError] = useState("");

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewTab, setPreviewTab] = useState(0);
    const [previewBundleId, setPreviewBundleId] = useState(null);

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/ClassResults/school/${schoolId}`, {
                params: buildTermRecordsQuery({
                    recordsScope,
                    extra: { status: "Submitted" },
                }),
            });
            setRows(Array.isArray(data) ? data : []);
            setLoadError("");
        } catch (err) {
            setRows([]);
            setLoadError(
                err.response?.data?.message ||
                    "Could not load pending class results. Check that ClassResults is deployed on Supabase.",
            );
        } finally {
            setLoading(false);
        }
    }, [schoolId, recordsScope]);

    useEffect(() => {
        load();
    }, [load]);

    const handleApprove = async (id) => {
        setActing(true);
        try {
            const { data } = await axios.put(`${getApiBaseUrl()}/ClassResults/bundle/${id}/approve`, {
                approvedById: adminId,
            });
            setMessage(
                data?.message ||
                    "Approved. Term data moved to Results records; teachers can start the next term with a blank slate.",
            );
            setPopupSeverity("success");
            setShowPopup(true);
            setPreviewOpen(false);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setActing(false);
        }
    };

    const openPreview = async (bundleId) => {
        setPreviewBundleId(bundleId);
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreview(null);
        setPreviewTab(0);
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/ClassResults/bundle/${bundleId}`);
            setPreview(data);
            if (data?.reconciledCount > 0) {
                await load();
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load results preview");
            setPopupSeverity("error");
            setShowPopup(true);
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleRejectConfirm = async () => {
        if (!rejectId) return;
        setActing(true);
        try {
            await axios.put(`${getApiBaseUrl()}/ClassResults/bundle/${rejectId}/reject`, {
                approvedById: adminId,
                rejectionNote,
            });
            setRejectOpen(false);
            setPreviewOpen(false);
            setMessage(`Class results sent back to ${homeroomTerm.theRole}.`);
            setPopupSeverity("success");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setActing(false);
        }
    };

    const previewSubjects = Array.isArray(preview?.subjects) ? preview.subjects : [];
    const educationLevel = preview?.class?.educationLevel || "Primary";
    const levelCode = preview?.class?.levelCode || "";

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h5" fontWeight={800} gutterBottom>
                Class results review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review marks before approving. Use <strong>View results</strong> to check every subject and pupil,
                then approve to publish report cards on the student portal.
            </Typography>

            <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <TermRecordsScopeAlert
                scope={recordsScope}
                currentTermLabel={currentTermLabel}
                sectionLabel="class results packages"
            />

            {loadError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Class</StyledTableCell>
                            <StyledTableCell>{homeroomTerm.roleTitleCaps}</StyledTableCell>
                            <StyledTableCell>Term</StyledTableCell>
                            <StyledTableCell>Period</StyledTableCell>
                            <StyledTableCell>Subjects</StyledTableCell>
                            <StyledTableCell align="right">Actions</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? Array.from({ length: 3 }).map((_, i) => (
                                  <StyledTableRow key={i}>
                                      <StyledTableCell colSpan={6}>
                                          <Skeleton height={32} />
                                      </StyledTableCell>
                                  </StyledTableRow>
                              ))
                            : null}
                        {!loading && rows.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    No class results awaiting approval.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : null}
                        {!loading &&
                            rows.map((row) => {
                                const id = row._id || row.id;
                                const subCount = row.bundleSubjectCount ?? row.marksSubmissions?.length ?? 0;
                                const classSubCount = row.classSubjectCount;
                                const incomplete =
                                    row.incompletePackage ||
                                    (classSubCount != null && subCount < classSubCount);
                                return (
                                    <StyledTableRow key={id}>
                                        <StyledTableCell>{row.class?.sclassName}</StyledTableCell>
                                        <StyledTableCell>{row.formTeacher?.name}</StyledTableCell>
                                        <StyledTableCell>{row.term}</StyledTableCell>
                                        <StyledTableCell>
                                            <Chip size="small" label={reportPeriodDisplayLabel(row.reportPeriod)} />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {subCount}
                                            {classSubCount != null ? ` / ${classSubCount}` : ""}
                                            {incomplete ? (
                                                <Chip
                                                    size="small"
                                                    color="warning"
                                                    label="Incomplete"
                                                    sx={{ ml: 1 }}
                                                />
                                            ) : null}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<VisibilityOutlinedIcon />}
                                                    disabled={acting}
                                                    onClick={() => openPreview(id)}
                                                >
                                                    View results
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={acting}
                                                    onClick={() => handleApprove(id)}
                                                >
                                                    Approve &amp; publish
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    disabled={acting}
                                                    onClick={() => {
                                                        setRejectId(id);
                                                        setRejectOpen(true);
                                                    }}
                                                >
                                                    Reject
                                                </Button>
                                            </Stack>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Review class results
                    {preview?.class?.sclassName ? ` — ${preview.class.sclassName}` : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading ? (
                        <Skeleton variant="rounded" height={280} />
                    ) : preview ? (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {preview.term} · {preview.academicYear} · {reportPeriodDisplayLabel(preview.reportPeriod)}
                                {preview.formTeacher?.name
                                    ? ` · ${homeroomTerm.roleTitleCaps}: ${preview.formTeacher.name}`
                                    : ""}
                            </Typography>

                            {preview.reconciledCount > 0 ? (
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    {preview.reconciledCount} additional accepted subject(s) were linked to this
                                    package automatically.
                                </Alert>
                            ) : null}

                            {preview.incompletePackage ? (
                                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                    This package has {preview.bundleSubjectCount} of{" "}
                                    {preview.classSubjectCount} class subjects. Ask the {homeroomTerm.theRole} to send
                                    the remaining subjects, or reject and resubmit when complete.
                                </Alert>
                            ) : null}

                            {previewSubjects.length === 0 ? (
                                <Alert severity="warning">No subject marks in this package yet.</Alert>
                            ) : (
                                <>
                                    <Tabs
                                        value={previewTab}
                                        onChange={(_, v) => setPreviewTab(v)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {previewSubjects.map((sub, idx) => (
                                            <Tab key={sub.submissionId || idx} label={sub.subName} />
                                        ))}
                                    </Tabs>
                                    {previewSubjects.map((sub, idx) =>
                                        previewTab === idx ? (
                                            <Box key={sub.submissionId || idx}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Teacher: {sub.teacherName}
                                                </Typography>
                                                <ResponsiveTableWrap>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Student ID</TableCell>
                                                                <TableCell>Name</TableCell>
                                                                <TableCell align="right">Marks %</TableCell>
                                                                <TableCell align="center">Grade</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {(sub.students || []).map((st) => {
                                                                const grade = getMalawiGrade(
                                                                    st.marksObtained,
                                                                    educationLevel,
                                                                    levelCode,
                                                                    preview.class?.sclassName,
                                                                );
                                                                return (
                                                                    <TableRow key={st.studentId}>
                                                                        <TableCell>{st.rollNum}</TableCell>
                                                                        <TableCell>{st.name}</TableCell>
                                                                        <TableCell align="right">
                                                                            {st.marksObtained}
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            {grade.grade}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </ResponsiveTableWrap>
                                            </Box>
                                        ) : null,
                                    )}
                                </>
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    {previewBundleId ? (
                        <>
                            <Button
                                color="error"
                                onClick={() => {
                                    setRejectId(previewBundleId);
                                    setRejectOpen(true);
                                }}
                                disabled={acting}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                disabled={acting || previewLoading || !previewSubjects.length}
                                onClick={() => handleApprove(previewBundleId)}
                            >
                                Approve &amp; publish
                            </Button>
                        </>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject class results</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={`Note to ${homeroomTerm.theRole}`}
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleRejectConfirm} disabled={acting}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup
                message={message}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                severity={popupSeverity}
            />
        </Container>
    );
}
